/* eslint-disable unicorn/prefer-module */
/* eslint-disable max-len */
/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getClientes, getContratos, getIntermediarios, getNegociosCarteleria, getOportunidades, getOportunidadesBTL, getUsers, log } from "../../../utils";
import { client } from "../../../utils/supabase";

const possibleYears = ["2024", "2025", "2026", "2027"];

export const useVFDataLoading = (pais, getUFValue, ratesLoaded) => {
  const [todasOportunidades, setTodasOportunidades] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [ejecutivosUnicos, setEjecutivosUnicos] = useState([]);
  const [siiMap, setSiiMap] = useState({});

  useEffect(() => {
    if (!ratesLoaded) return;
    const initialize = async () => {
      try {
        const [allOportunidades, allIntermediarios, allClientes, allUsuarios, allContratos, allNegociosCarteleria, edificiosRes] = await Promise.all([
          getOportunidades({ pais }),
          getIntermediarios({ pais }),
          getClientes({ pais }),
          getUsers(),
          getContratos({ pais }),
          getNegociosCarteleria({ pais }),
          client.from("edificios").select("id, edificio").eq("pais", pais),
        ]);
        const edificiosMap = (edificiosRes.data || []).reduce((acc, e) => ({ ...acc, [e.id]: e.edificio }), {});
        const clientesMap = allClientes.reduce((acc, c) => ({ ...acc, [c.id]: c.nombre }), {});
        const allOportunidadesBTLPromises = possibleYears.map(ano => getOportunidadesBTL({ pais, ano }));
        const allOportunidadesBTLArrays = await Promise.all(allOportunidadesBTLPromises);
        const allOportunidadesBTL = allOportunidadesBTLArrays.flat();

        // Deduplicate BTL opportunities by ID
        const seenIds = new Set();
        const uniqueOportunidadesBTL = allOportunidadesBTL.filter((btl) => {
          if (seenIds.has(btl.id)) {
            return false;
          }
          seenIds.add(btl.id);
          return true;
        });

        // Mapear oportunidades regulares con el campo ejecutivo
        const mappedOportunidades = allOportunidades.map((op) => {
          let mes = (new Date().getMonth() + 1).toString().padStart(2, "0");
          let ano = new Date().getFullYear().toString();
          if (op.mes_factura && typeof op.mes_factura === "string") {
            const [month, year] = op.mes_factura.split("-");
            if (month && year) {
              mes = month.padStart(2, "0");
              ano = year;
            }
          }
          return {
            ...op,
            type: "pauta",
            ejecutivo: op.ejecutivo ? parseInt(op.ejecutivo, 10) : null,
            mes,
            ano,
          };
        });

        // Mapear oportunidades BTL con executive_responsible
        const filteredOportunidadesBTL = uniqueOportunidadesBTL.filter(
          (btl) => btl.status === 100 && !btl.eliminada && btl.pais === pais
        );
        const mappedOportunidadesBTL = filteredOportunidadesBTL.map((btl) => {
          let mes = (new Date().getMonth() + 1).toString().padStart(2, "0");
          let ano = new Date().getFullYear().toString();
          if (btl.mes_factura && typeof btl.mes_factura === "string") {
            const [month, year] = btl.mes_factura.split("-");
            if (month && year) {
              mes = month.padStart(2, "0");
              ano = year;
            }
          }
          return {
            id: btl.id,
            client_id: btl.client_id,
            importe: btl.total_investment,
            comision: btl.total_commission,
            facturafecha: btl.facturafecha,
            facturanumero: btl.facturanumero,
            facturafechacobro: btl.facturafechacobro,
            mes,
            ano,
            estatus: "100%",
            eliminada: btl.eliminada,
            cliente: allClientes.find((c) => c.id === btl.client_id)?.nombre || "Desconocido",
            type: "BTL",
            moneda_dolar: btl.moneda_dolar || false,
            ejecutivo: btl.executive_responsible ? parseInt(btl.executive_responsible, 10) : null,
          };
        });

        // Explotar contratos: cada mes del JSONB → una fila facturable
        const mappedContratos = (allContratos || [])
          .filter((c) => !c.eliminada && c.estatus === "100%" && c.pais === pais)
          .flatMap((c) =>
            (c.meses || []).map((mesObj, idx) => {
              const [mesStr, anoStr] = (mesObj.mes || "01-2026").split("-");
              return {
                id: `contrato-${c.id}-${idx}`,
                contrato_id: c.id,
                mes_index: idx,
                client_id: c.client_id,
                importe: mesObj.importe || 0,
                comision: null,
                facturafecha: mesObj.facturafecha,
                facturanumero: mesObj.facturanumero,
                facturafechacobro: mesObj.facturafechacobro,
                plazocobro: mesObj.plazocobro,
                mes: mesStr,
                ano: anoStr,
                estatus: "100%",
                eliminada: false,
                cliente: allClientes.find((cl) => cl.id === c.client_id)?.nombre || c.cliente || "Desconocido",
                type: "contrato",
                moneda_dolar: c.moneda_dolar || false,
                ejecutivo: c.ejecutivo ? parseInt(c.ejecutivo, 10) : null,
                campana: c.campana,
                label_extra: `Mes ${idx + 1}/${(c.meses || []).length}`,
                personas_comprometidas: c.personas_comprometidas,
                edificios_ids: mesObj.edificios_ids || null,
                pantallas_ascensores: mesObj.pantallas_ascensores || "",
                pantallas_espera: mesObj.pantallas_espera || "",
                pantallas_circulacion: mesObj.pantallas_circulacion || "",
                pantallas_cowork: mesObj.pantallas_cowork || "",
                pantallastotales: mesObj.pantallas_totales || 0,
                display_id: c.display_id || null,
              };
            })
          );

        // Generar filas de cartelería (HW-CAPEX, HW-OPEX, Gestión)
        const mappedCarteleria = [];
        const elegibles = allNegociosCarteleria.filter((n) =>
          ["activo", "100%"].includes((n.estado || "").toLowerCase())
        );
        elegibles.forEach((n) => {
          const pantallas = parseInt(n.cantidad_pantallas, 10) || 1;
          const plazo = parseInt(n.plazo_contrato_meses, 10) || 0;
          const nombreCliente = n.tipo_vertical === "TD"
            ? (clientesMap[n.referencia_id] || edificiosMap[n.referencia_id] || n.nombre_proyecto || "—")
            : (edificiosMap[n.referencia_id] || clientesMap[n.referencia_id] || n.nombre_proyecto || "—");
          const ejecutivoId = n.ejecutivo ? parseInt(n.ejecutivo, 10) : null;
          const mesesFact = n.meses_facturacion
            ? (typeof n.meses_facturacion === "string" ? JSON.parse(n.meses_facturacion) : n.meses_facturacion)
            : null;

          // Total fee UF del negocio por mes (para prorrateo SII)
          const feeGestionNeg = parseFloat(n.fee_gestion_unitario) || 0;
          const feeOpexNeg = (n.incluye_hardware && n.modalidad === "OPEX") ? (parseFloat(n.cuota_hardware_opex) || 0) : 0;
          const feeContNeg = n.incluye_contenidos ? (parseFloat(n.fee_contenidos_unitario) || 0) : 0;

          // --- HW-CAPEX: una fila por entry en detalle_pagos_hardware ---
          if (n.incluye_hardware && n.modalidad === "CAPEX") {
            const detalle = typeof n.detalle_pagos_hardware === "string"
              ? JSON.parse(n.detalle_pagos_hardware || "[]")
              : (n.detalle_pagos_hardware || []);
            if (detalle.length > 0) {
              detalle.forEach((entry, idx) => {
                const monto = parseFloat(entry.monto) || 0;
                if (monto <= 0) return;
                const fechaRef = entry.facturafecha || entry.fecha_factura || null;
                const [y, m] = fechaRef ? fechaRef.split("-") : [new Date().getFullYear().toString(), "01"];
                let importeFinal = monto;
                let monedaDolar = n.moneda_hardware === "USD";
                if (n.moneda_hardware === "UF") {
                  importeFinal = monto * (getUFValue(fechaRef || new Date().toISOString().split("T")[0]) || 1);
                  monedaDolar = false;
                }
                mappedCarteleria.push({
                  id: `hw-capex-${n.id}-${idx}`, negocio_id: n.id, display_id: n.id,
                  entry_index: idx, client_id: null,
                  importe: importeFinal, comision: null,
                  facturafecha: entry.facturafecha || null, facturanumero: entry.facturanumero || null,
                  facturafechacobro: entry.facturafechacobro || null, plazocobro: entry.plazocobro || null,
                  mes: (m || "01").padStart(2, "0"), ano: y || new Date().getFullYear().toString(),
                  estatus: "100%", eliminada: false, cliente: nombreCliente,
                  type: "HW-CAPEX", moneda_dolar: monedaDolar, ejecutivo: ejecutivoId,
                  label_extra: `Pago ${idx + 1}/${detalle.length}`,
                  valor_cuota: monto,
                  moneda_cuota: n.moneda_hardware,
                });
              });
            } else if (parseFloat(n.monto_hardware) > 0) {
              // CAPEX sin detalle: fila única con monto_hardware total
              const fechaRef = n.fecha_cierre || n.fecha_inicio || null;
              const [y, m] = fechaRef ? fechaRef.split("-") : [new Date().getFullYear().toString(), "01"];
              let importeFinal = parseFloat(n.monto_hardware);
              let monedaDolar = n.moneda_hardware === "USD";
              if (n.moneda_hardware === "UF") {
                importeFinal = importeFinal * (getUFValue(fechaRef || new Date().toISOString().split("T")[0]) || 1);
                monedaDolar = false;
              }
              mappedCarteleria.push({
                id: `hw-capex-${n.id}-0`, negocio_id: n.id, display_id: n.id,
                entry_index: 0, client_id: null,
                importe: importeFinal, comision: null,
                facturafecha: null, facturanumero: null, facturafechacobro: null, plazocobro: null,
                mes: (m || "01").padStart(2, "0"), ano: y || new Date().getFullYear().toString(),
                estatus: "100%", eliminada: false, cliente: nombreCliente,
                type: "HW-CAPEX", moneda_dolar: monedaDolar, ejecutivo: ejecutivoId,
                label_extra: "Pago único",
                valor_cuota: parseFloat(n.monto_hardware),
                moneda_cuota: n.moneda_hardware,
              });
            }
          }

          // --- HW-OPEX: una fila por mes del contrato ---
          if (n.incluye_hardware && n.modalidad === "OPEX" && plazo > 0 && n.fecha_inicio) {
            const cuotaOpex = parseFloat(n.cuota_hardware_opex) || 0;
            if (cuotaOpex > 0) {
              const startDate = new Date(n.fecha_inicio + "T00:00:00");
              for (let i = 0; i < plazo; i++) {
                const mesDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
                const mesStr = (mesDate.getMonth() + 1).toString().padStart(2, "0");
                const anoStr = mesDate.getFullYear().toString();
                const mesFact = mesesFact?.find((mf) => mf.mes === mesStr && mf.ano === anoStr && mf.tipo === "opex");
                const mesFactIdx = mesesFact?.findIndex((mf) => mf.mes === mesStr && mf.ano === anoStr && mf.tipo === "opex");
                let importeBase = cuotaOpex * pantallas;
                let monedaDolar = n.moneda_hardware === "USD";
                if (n.moneda_hardware === "UF") {
                  const fechaRef = mesFact?.facturafecha || `${anoStr}-${mesStr}-01`;
                  importeBase = importeBase * (getUFValue(fechaRef) || 1);
                  monedaDolar = false;
                }
                // Proporción de OPEX respecto al total del negocio (para prorrateo SII)
                const totalFeeNeg = (feeOpexNeg + feeGestionNeg + feeContNeg) * pantallas;
                const feeProporcion = totalFeeNeg > 0 ? (cuotaOpex * pantallas) / totalFeeNeg : 1;

                mappedCarteleria.push({
                  id: `hw-opex-${n.id}-${i}`, negocio_id: n.id, display_id: n.id, mes_index: i, sii_ref_index: mesFactIdx >= 0 ? mesFactIdx : null, client_id: null,
                  importe: importeBase, comision: null,
                  feeOpexUF: cuotaOpex * pantallas, feeProporcion,
                  facturafecha: mesFact?.facturafecha || null, facturanumero: mesFact?.facturanumero || null,
                  facturafechacobro: mesFact?.facturafechacobro || null, plazocobro: mesFact?.plazocobro || null,
                  mes: mesStr, ano: anoStr, estatus: "100%", eliminada: false, cliente: nombreCliente,
                  type: "HW-OPEX", moneda_dolar: monedaDolar, ejecutivo: ejecutivoId,
                  label_extra: `Mes ${i + 1}/${plazo}`,
                  valor_cuota: cuotaOpex * pantallas,
                  moneda_cuota: n.moneda_hardware,
                });
              }
            }
          }

          // --- Gestión & Contenidos: una fila por mes (solo EO/TD, no OT) ---
          if (n.tipo_vertical !== "OT" && plazo > 0 && n.fecha_inicio) {
            const feeGestion = parseFloat(n.fee_gestion_unitario) || 0;
            const feeContenidosBase = n.incluye_contenidos ? (parseFloat(n.fee_contenidos_unitario) || 0) : 0;
            const contInicio = n.contenidos_fecha_inicio ? new Date(n.contenidos_fecha_inicio + "T00:00:00") : null;
            const contFin = n.contenidos_fecha_fin ? new Date(n.contenidos_fecha_fin + "T00:00:00") : null;

            if (feeGestion > 0 || feeContenidosBase > 0) {
              const startDate = new Date(n.fecha_inicio + "T00:00:00");
              for (let i = 0; i < plazo; i++) {
                const mesDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
                const mesStr = (mesDate.getMonth() + 1).toString().padStart(2, "0");
                const anoStr = mesDate.getFullYear().toString();

                // Contenidos solo aplica dentro del rango de fechas (si está definido)
                const mesEnRangoContenidos = feeContenidosBase > 0 && (
                  !contInicio || !contFin || (mesDate >= contInicio && mesDate <= contFin)
                );
                const feeContenidos = mesEnRangoContenidos ? feeContenidosBase : 0;

                // Calcular importe según moneda
                const mesFact = mesesFact?.find((mf) => mf.mes === mesStr && mf.ano === anoStr && mf.tipo === "gestion");
                const mesFactIdx = mesesFact?.findIndex((mf) => mf.mes === mesStr && mf.ano === anoStr && mf.tipo === "gestion");
                const fechaRef = mesFact?.facturafecha || `${anoStr}-${mesStr}-01`;

                let importeGestion = feeGestion * pantallas;
                let importeContenidos = feeContenidos * pantallas;
                let monedaDolar = n.moneda_gestion === "USD";

                if (n.moneda_gestion === "UF") {
                  const ufVal = getUFValue(fechaRef) || 1;
                  importeGestion = importeGestion * ufVal;
                  monedaDolar = false;
                }
                // Contenidos puede tener moneda diferente (CLP vs UF)
                if (mesEnRangoContenidos && n.moneda_contenidos === "UF") {
                  const ufVal = getUFValue(fechaRef) || 1;
                  importeContenidos = importeContenidos * ufVal;
                } // Si moneda_contenidos es CLP, importeContenidos ya está en CLP

                const importeBase = importeGestion + importeContenidos;
                const feeTotal = (feeGestion * pantallas) + (feeContenidos * pantallas);

                // Proporción de gestión+contenidos respecto al total del negocio (para prorrateo SII)
                const totalFeeNeg = (feeOpexNeg + feeGestionNeg + (mesEnRangoContenidos ? feeContNeg : 0)) * pantallas;
                const feeProporcion = totalFeeNeg > 0 ? feeTotal / totalFeeNeg : 1;

                mappedCarteleria.push({
                  id: `gestion-${n.id}-${i}`, negocio_id: n.id, display_id: n.id, mes_index: i, sii_ref_index: mesFactIdx >= 0 ? mesFactIdx : null, client_id: null,
                  importe: importeBase, comision: null,
                  importeGestion, importeContenidos, feeProporcion,
                  feeGestionUF: n.moneda_gestion === "UF" ? feeGestion * pantallas : null,
                  feeContenidosUF: mesEnRangoContenidos && n.moneda_contenidos === "UF" ? feeContenidos * pantallas : null,
                  feeContenidosCLP: mesEnRangoContenidos && n.moneda_contenidos === "CLP" ? feeContenidos * pantallas : null,
                  facturafecha: mesFact?.facturafecha || null, facturanumero: mesFact?.facturanumero || null,
                  facturafechacobro: mesFact?.facturafechacobro || null, plazocobro: mesFact?.plazocobro || null,
                  mes: mesStr, ano: anoStr, estatus: "100%", eliminada: false, cliente: nombreCliente,
                  type: "Gestión", moneda_dolar: monedaDolar, ejecutivo: ejecutivoId,
                  label_extra: `${i + 1}/${plazo}`,
                  valor_cuota: feeTotal, moneda_cuota: n.moneda_gestion,
                  incluye_contenidos: mesEnRangoContenidos,
                });
              }
            }
          }
        });

        // Cargar vinculaciones SII con detalle por concepto
        const { data: siiVinculadas } = await client
          .from("facturas_sii_emitidas")
          .select(
            "folio, id, fecha_emision, monto_neto, monto_exento, monto_total, tipo_dte, plazocobro, facturafechacobro, " +
              "facturas_sii_vinculaciones(venta_tipo, venta_ref_id, venta_ref_index)"
          )
          .eq("pais", pais)
          .eq("estado_vinculacion", "vinculada");

        const siiMapData = { _byRef: {} };
        (siiVinculadas || []).forEach((f) => {
          const montoNeto = f.monto_neto || f.monto_exento || 0;
          siiMapData[String(f.folio)] = {
            facturaId: f.id,
            vinculaciones: f.facturas_sii_vinculaciones || [],
            plazocobro: f.plazocobro ?? null,
            facturafechacobro: f.facturafechacobro ?? null,
          };
          (f.facturas_sii_vinculaciones || []).forEach((v) => {
            const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
            siiMapData._byRef[key] = {
              facturaId: f.id,
              folio: f.folio,
              fechaEmision: f.fecha_emision,
              montoNeto,
              montoTotal: f.monto_total,
              tipoDte: f.tipo_dte,
              plazocobro: f.plazocobro ?? null,
              facturafechacobro: f.facturafechacobro ?? null,
            };
          });
        });
        setSiiMap(siiMapData);

        // Helper local: key para _byRef según tipo de oportunidad
        const refKeyForOp = (op) => {
          if (op.negocio_id && op.sii_ref_index != null) return `negocios_carteleria:${op.negocio_id}:${op.sii_ref_index}`;
          if (op.type === "pauta") return `sales:${op.id}:`;
          if (op.type === "BTL") return `sales_btl:${op.id}:`;
          if (op.type === "contrato") return `sales_contratos:${op.contrato_id}:${op.mes_index ?? ""}`;
          return null;
        };

        // Override plazocobro y facturafechacobro desde SII si hay vinculación
        const injectSIIDates = (op) => {
          const key = refKeyForOp(op);
          const bySII = (key && siiMapData._byRef[key]) || (op.facturanumero && siiMapData[String(op.facturanumero)]) || null;
          if (!bySII) return op;
          return {
            ...op,
            plazocobro: bySII.plazocobro ?? op.plazocobro,
            facturafechacobro: bySII.facturafechacobro ?? op.facturafechacobro,
          };
        };

        const combinedOportunidades = [
          ...mappedOportunidades,
          ...mappedOportunidadesBTL,
          ...mappedContratos,
          ...mappedCarteleria,
        ].map(injectSIIDates);
        setTodasOportunidades(combinedOportunidades);
        setIntermediarios(allIntermediarios);
        setClientes(allClientes);
        setUsuarios(allUsuarios);

        // Mapear ejecutivos únicos basados en los IDs con ventas al 100%
        const uniqueEjecutivoIds = [...new Set(combinedOportunidades.map((op) => op.ejecutivo).filter((id) => id !== null))];
        const ejecutivosUnicosList = uniqueEjecutivoIds
          .map((ejecutivoId) => {
            const usuario = allUsuarios.find((u) => parseInt(u.id, 10) === ejecutivoId);
            return usuario ? { id: ejecutivoId, nombre: usuario.nombre } : null;
          })
          .filter((u) => u !== null)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setEjecutivosUnicos(ejecutivosUnicosList);
      } catch (error) {
        console.error("Error al inicializar datos:", error);
        log.error("initialize", error);
        toast.error("Error al cargar los datos iniciales");
      }
    };
    initialize();
  }, [pais, ratesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const recargarSiiMap = async () => {
    const { data } = await client
      .from("facturas_sii_emitidas")
      .select(
        "folio, id, fecha_emision, monto_neto, monto_exento, monto_total, tipo_dte, plazocobro, facturafechacobro, " +
          "facturas_sii_vinculaciones(venta_tipo, venta_ref_id, venta_ref_index)"
      )
      .eq("pais", pais)
      .eq("estado_vinculacion", "vinculada");
    const map = { _byRef: {} };
    (data || []).forEach((f) => {
      const montoNeto = f.monto_neto || f.monto_exento || 0;
      map[String(f.folio)] = {
        facturaId: f.id,
        vinculaciones: f.facturas_sii_vinculaciones || [],
        plazocobro: f.plazocobro ?? null,
        facturafechacobro: f.facturafechacobro ?? null,
      };
      (f.facturas_sii_vinculaciones || []).forEach((v) => {
        const key = `${v.venta_tipo}:${v.venta_ref_id}:${v.venta_ref_index ?? ""}`;
        map._byRef[key] = {
          facturaId: f.id,
          folio: f.folio,
          fechaEmision: f.fecha_emision,
          montoNeto,
          montoTotal: f.monto_total,
          tipoDte: f.tipo_dte,
          plazocobro: f.plazocobro ?? null,
          facturafechacobro: f.facturafechacobro ?? null,
        };
      });
    });
    setSiiMap(map);
  };

  const isDataLoaded = todasOportunidades.length > 0
    && intermediarios.length > 0
    && clientes.length > 0
    && usuarios.length > 0;

  return {
    todasOportunidades,
    setTodasOportunidades,
    intermediarios,
    clientes,
    usuarios,
    ejecutivosUnicos,
    siiMap,
    recargarSiiMap,
    isDataLoaded,
  };
};
